// Generierte Teilegruppen (Korpus, Schublade, Tür) nachträglich bearbeiten,
// ohne die Handarbeit daran zu verlieren. Engine-frei und rein funktional.
//
// Beim Übernehmen geänderter Parameter baut der Generator die Gruppe komplett
// neu auf. Alles, was seit dem Erzeugen von Hand (oder per Assistent) an den
// Teilen entstanden ist — Ausschnitte/Bohrungen, Umleimer, Ecken, Nut/Falz,
// Gehrungen, Constraints, Beschläge, umbenannte Teile, abweichende Materialien —
// ginge dabei verloren. Hier werden alte und neue Teile über den stabilen
// Rollen-Schlüssel `meta.role` einander zugeordnet und die Handarbeit auf das
// neue Teil übertragen. Bei geschrumpften Teilen werden Bearbeitungen geklemmt;
// nur geometrisch Unmögliches fällt weg und wird gezählt.
import { clampCutout, isCircle, isEndGrain, cutoutAxis } from "./cutouts"
import { clampGroove, crossDimension, isFalz } from "./machinings"
import { clampCorners } from "./corners"

// Handarbeits-Felder in part.meta. Die Liste ist dokumentarisch; die Regel
// arbeitet über das Komplement (siehe preserveManualMeta): meta ist schemafrei,
// deshalb bleibt ALLES erhalten, was nicht nachweislich dem Generator gehört
// (z. B. auch meta.group oder meta.hidden).
export const MANUAL_META_KEYS = [
  "cutouts", "edges", "corners", "grooves", "miters", "constraints", "hardware"
]

// Felder, die der Generator selbst setzt — sie kommen immer vom NEUEN Teil.
// `part_type` (editor/part_types.js) gehört dazu: Wer eine Rückwand abwählt
// und die Gruppe neu bauen lässt, soll nicht den alten Bauteil-Typ erben.
// NICHT enthalten ist `belongs_to_cabinet`: die Zuordnung Front → Korpus
// entsteht beim Erzeugen aus der Auswahl und kennt der Generator beim
// Bearbeiten nicht mehr — sie bleibt über die Komplement-Regel erhalten.
export const GENERATOR_META_KEYS = [
  "cabinet", "cabinet_id", "drawer", "drawer_id", "door", "door_id", "role", "attached_to", "part_type"
]

// Listen, in denen Generator- und Handarbeits-Einträge nebeneinander liegen:
// Der Tür-Generator legt seine Topfbänder als Bohrung + Beschlagzeile mit
// source "hardware" an. Diese Einträge gehören ihm (Anschlagseite/Maße ändern
// sich mit den Parametern), alles andere — Verbindungs-Assistent ("joinery")
// und von Hand gesetzte Beschläge (eigene source) — ist Handarbeit.
const SOURCED_LIST_KEYS = [ "cutouts", "hardware" ]
const GENERATED_SOURCE = "hardware"

// Neues meta für ein neu gebautes Teil: Generator-Felder vom NEUEN Teil,
// Handarbeit vom ALTEN.
export function preserveManualMeta(oldPart, newPart) {
  const oldMeta = oldPart?.meta ?? {}
  const newMeta = newPart?.meta ?? {}
  const meta = {}
  for (const [ key, value ] of Object.entries(oldMeta)) {
    if (GENERATOR_META_KEYS.includes(key)) continue
    meta[key] = value
  }
  // Alles, was der Generator gerade gesetzt hat, gewinnt — außer den
  // Handarbeits-Feldern, die bleiben beim Stand des alten Teils.
  for (const [ key, value ] of Object.entries(newMeta)) {
    if (MANUAL_META_KEYS.includes(key)) continue
    meta[key] = value
  }
  for (const key of SOURCED_LIST_KEYS) {
    const merged = [
      ...(newMeta[key] ?? []),
      ...(oldMeta[key] ?? []).filter((entry) => entry?.source !== GENERATED_SOURCE)
    ]
    if (merged.length > 0) meta[key] = merged
    else delete meta[key]
  }
  return meta
}

// Bearbeitungen auf die (ggf. neuen) Maße des Teils klemmen. Was auch geklemmt
// nicht mehr in die Platte passt, fällt weg und wird gezählt.
// Liefert { meta, dropped }.
export function clampManualMeta(part) {
  let meta = { ...(part.meta ?? {}) }
  let dropped = 0

  const cutouts = meta.cutouts ?? []
  if (cutouts.length > 0) {
    const kept = cutouts.map((cutout) => clampCutout(cutout, part)).filter((cutout) => cutoutFits(cutout, part))
    dropped += cutouts.length - kept.length
    if (kept.length > 0) meta.cutouts = kept
    else delete meta.cutouts
  }

  const grooves = meta.grooves ?? []
  if (grooves.length > 0) {
    const kept = grooves.map((groove) => clampGroove(groove, part)).filter((groove) => grooveFits(groove, part))
    dropped += grooves.length - kept.length
    if (kept.length > 0) meta.grooves = kept
    else delete meta.grooves
  }

  const corners = meta.corners ?? {}
  if (Object.keys(corners).length > 0) {
    const clamped = clampCorners({ ...part, meta })
    const kept = Object.entries(clamped.corners ?? {}).filter(([ , corner ]) => corner.size_mm >= 1)
    dropped += Object.keys(corners).length - kept.length
    meta = { ...clamped }
    if (kept.length > 0) meta.corners = Object.fromEntries(kept)
    else delete meta.corners
  }

  return { meta, dropped }
}

// Passt die (bereits geklemmte) Bearbeitung noch vollständig in die Platte?
// clampCutout/clampGroove klemmen immer auf ein Mindestmaß — bei sehr kleinen
// Teilen kann das Ergebnis über den Rand hinausragen, dann ist es unmöglich.
function cutoutFits(cutout, part) {
  if (isEndGrain(cutout)) {
    const axis = cutoutAxis(cutout)
    const along = axis === "x" ? part.length_mm : part.width_mm
    const cross = axis === "x" ? part.width_mm : part.length_mm
    const crossKey = axis === "x" ? "z_mm" : "x_mm"
    const radius = cutout.diameter_mm / 2
    return cutout.diameter_mm > 0 && cutout.depth_mm > 0 && cutout.depth_mm <= along &&
      Math.abs(cutout[crossKey] ?? 0) + radius <= cross / 2 &&
      Math.abs(cutout.y_mm ?? 0) + radius <= part.thickness_mm / 2
  }
  if (isCircle(cutout)) {
    const radius = cutout.diameter_mm / 2
    return cutout.diameter_mm > 0 &&
      Math.abs(cutout.x_mm ?? 0) + radius <= part.length_mm / 2 &&
      Math.abs(cutout.z_mm ?? 0) + radius <= part.width_mm / 2
  }
  return cutout.length_mm > 0 && cutout.width_mm > 0 &&
    Math.abs(cutout.x_mm ?? 0) + cutout.length_mm / 2 <= part.length_mm / 2 &&
    Math.abs(cutout.z_mm ?? 0) + cutout.width_mm / 2 <= part.width_mm / 2
}

function grooveFits(groove, part) {
  if (!(groove.width_mm > 0) || !(groove.depth_mm > 0)) return false
  if (groove.depth_mm >= part.thickness_mm) return false
  const cross = crossDimension(groove, part)
  if (isFalz(groove)) return groove.width_mm <= cross
  return groove.offset_mm - groove.width_mm / 2 >= 0 && groove.offset_mm + groove.width_mm / 2 <= cross
}

// Alte Gruppenteile den neu gebauten zuordnen und die Handarbeit übernehmen.
// Zuordnung über meta.role (stabil über Parameteränderungen hinweg); für
// Altbestände ohne role greift der Rückfall über Name + Reihenfolge.
// Liefert { parts, dropped } — parts sind neue Objekte, die Eingaben bleiben
// unberührt.
export function mergeGeneratedGroup(oldParts = [], newParts = []) {
  const remaining = oldParts.filter(Boolean)
  const matches = new Map()
  const take = (index) => remaining.splice(index, 1)[0]

  newParts.forEach((newPart, i) => {
    const role = newPart?.meta?.role
    if (!role) return
    const index = remaining.findIndex((old) => old.meta?.role === role)
    if (index >= 0) matches.set(i, take(index))
  })
  newParts.forEach((newPart, i) => {
    if (matches.has(i)) return
    const index = remaining.findIndex((old) => !old.meta?.role && old.name === newPart?.name)
    if (index >= 0) matches.set(i, take(index))
  })

  // Beim Neubau bekommt jedes Teil eine neue client_id. Constraints auf ein
  // anderes Teil DERSELBEN Gruppe würden damit ins Leere zeigen — sie werden
  // auf die neue Id umgebogen bzw. fallen weg, wenn es das Teil nicht mehr gibt.
  const groupIds = new Set(oldParts.filter(Boolean).map((part) => part.client_id))
  const idMap = new Map()
  for (const [ i, old ] of matches) idMap.set(old.client_id, newParts[i].client_id)

  let dropped = 0
  const parts = newParts.map((newPart, i) => {
    const old = matches.get(i)
    if (!old) return newPart
    const merged = { ...newPart, meta: remapConstraints(preserveManualMeta(old, newPart), idMap, groupIds) }
    // Umbenanntes Teil und von Hand gewähltes Material behalten — der Generator
    // liefert für beides nur seinen Vorgabewert.
    if (old.name && old.name !== newPart.name) merged.name = old.name
    if (old.material_id != null && old.material_id !== newPart.material_id) merged.material_id = old.material_id
    const clamped = clampManualMeta(merged)
    merged.meta = clamped.meta
    dropped += clamped.dropped
    return merged
  })

  return { parts, dropped }
}

function remapConstraints(meta, idMap, groupIds) {
  const constraints = meta.constraints
  if (!Array.isArray(constraints) || constraints.length === 0) return meta
  const remapped = constraints
    .filter((constraint) => idMap.has(constraint.other) || !groupIds.has(constraint.other))
    .map((constraint) => idMap.has(constraint.other)
      ? { ...constraint, other: idMap.get(constraint.other) }
      : constraint)
  const next = { ...meta }
  if (remapped.length > 0) next.constraints = remapped
  else delete next.constraints
  return next
}

// Statuszeile für verworfene Bearbeitungen; null, wenn nichts weggefallen ist.
export function droppedMessage(dropped) {
  if (!dropped) return null
  return dropped === 1
    ? "1 Bearbeitung konnte nicht übernommen werden"
    : `${dropped} Bearbeitungen konnten nicht übernommen werden`
}
